<template>
  <div class="bg-truegray-700 min-h-screen">
    <!-- HEADING -->
    <div class="lg:h-72 flex justify-start items-end p-12 bg-truegray-100">
      <div
        class="border-l-4 pl-6 border-black flex flex-col justify-end items-end m-0 p-0 w-full"
      >
        <h1 class="w-full text-left">Site Settings</h1>
        <span class="text-md w-full text-left"
          >Customising your CareerWise site</span
        >
      </div>
    </div>

    <!-- CONTENT -->
    <div class="flex flex-col px-6 py-2 w-full">
      <div class="w-full flex justify-start py-6 lg:py-12">
        <div
          :style="{ height: 'max-content' }"
          class="w-16 rounded-l-xl overflow-hidden border-r flex-shrink-0"
        >
          <div
            v-for="tab in TABS"
            :key="tab.id"
            :class="[
              'flex items-center justify-center border-t bg-white text-black hover:text-primary h-16 transform transition-all',
              tab.disabled()
                ? 'opacity-25 cursor-not-allowed'
                : 'cursor-pointer',
              {
                'border-0': !isTabActive(tab.id),
                'text-primary': isTabActive(tab.id),
              },
            ]"
            :title="
              tab.disabled()
                ? 'You do not have permission to access this tab'
                : tab.title
            "
            @click="selectTab(tab.id)"
          >
            <span
              :class="!isTabActive(tab.id) ? 'opacity-50' : 'scale-125'"
              class="transition-all transform"
              v-html="tab.svg"
            />
          </div>
        </div>
        <keep-alive>
          <component :is="activeTab.component" :tab="activeTab" />
        </keep-alive>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, ref } from 'vue'
import { useStore } from '../../../../../js/store/index.js'
import TabBasics from './TabBasics.vue'
import TabCustomisation from './TabCustomisation.vue'
import TabAdmins from './TabAdmins.vue'

export default defineComponent({
  name: 'PageSettings',

  components: {
    TabBasics,
    TabCustomisation,
    TabAdmins,
  },

  setup() {
    const TABS = [
      {
        id: 0,
        title: 'School Info',
        disabled: () => false,
        component: 'TabBasics',
        svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4">
                <path fill-rule="evenodd" d="M7.84 1.804A1 1 0 0 1 8.82 1h2.36a1 1 0 0 1 .98.804l.331 1.652a6.993 6.993 0 0 1 1.929 1.115l1.598-.54a1 1 0 0 1 1.186.447l1.18 2.044a1 1 0 0 1-.205 1.251l-1.267 1.113a7.047 7.047 0 0 1 0 2.228l1.267 1.113a1 1 0 0 1 .206 1.25l-1.18 2.045a1 1 0 0 1-1.187.447l-1.598-.54a6.993 6.993 0 0 1-1.929 1.115l-.33 1.652a1 1 0 0 1-.98.804H8.82a1 1 0 0 1-.98-.804l-.331-1.652a6.993 6.993 0 0 1-1.929-1.115l-1.598.54a1 1 0 0 1-1.186-.447l-1.18-2.044a1 1 0 0 1 .205-1.251l1.267-1.114a7.05 7.05 0 0 1 0-2.227L1.821 7.773a1 1 0 0 1-.206-1.25l1.18-2.045a1 1 0 0 1 1.187-.447l1.598.54A6.992 6.992 0 0 1 7.51 3.456l.33-1.652ZM10 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" clip-rule="evenodd" />
              </svg>`,
      },
      {
        id: 1,
        title: 'Site Customisation',
        disabled: () => false,
        component: 'TabCustomisation',
        svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4">
                <path d="M15.993 1.385a1.87 1.87 0 0 1 2.623 2.622l-4.03 5.27a12.749 12.749 0 0 1-4.237 3.562 4.508 4.508 0 0 0-3.188-3.188 12.75 12.75 0 0 1 3.562-4.236l5.27-4.03ZM6 11a3 3 0 0 0-3 3 .5.5 0 0 1-.72.45.75.75 0 0 0-1.035.931A4.001 4.001 0 0 0 9 14.004V14a3.01 3.01 0 0 0-1.66-2.685A2.99 2.99 0 0 0 6 11Z" />
              </svg>`,
      },
      {
        id: 2,
        title: 'Admin Management',
        disabled: () => isCateRegionalAdmin.value,
        component: 'TabAdmins',
        svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4">
                <path d="M7 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM14.5 9a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM1.615 16.428a1.224 1.224 0 0 1-.569-1.175 6.002 6.002 0 0 1 11.908 0c.058.467-.172.92-.57 1.174A9.953 9.953 0 0 1 7 18a9.953 9.953 0 0 1-5.385-1.572ZM14.5 16h-.106c.07-.297.088-.611.048-.933a7.47 7.47 0 0 0-1.588-3.755 4.502 4.502 0 0 1 5.874 2.636.818.818 0 0 1-.36.98A7.465 7.465 0 0 1 14.5 16Z" />
              </svg>`,
      },
    ]

    const store = useStore()

    const isCateRegionalAdmin = computed(() => {
      return store.getters['shared/isCateRegionalAdmin']
    })

    const activeTabId = ref(0)
    const activeTab = computed(() => getTabById(activeTabId.value))

    const isTabActive = (tabId) => {
      return activeTabId.value === tabId
    }

    const getTabById = (tabId) => {
      return TABS.find((tab) => tab.id === tabId)
    }

    const selectTab = (tabId) => {
      const tab = getTabById(tabId)

      if (!tab.disabled()) {
        activeTabId.value = tabId
      }
    }

    return {
      TABS,
      isCateRegionalAdmin,
      activeTab,
      isTabActive,
      selectTab,
    }
  },
})
</script>
